<template>
    <b-modal id="schema-input-detail-modal"
             :title="modalTitle"
             hide-footer
             size="xl"
             modal-class="default-modal"
             @hide="onHide"
             @hidden="onHidden"
    >
        <div class="action-btns">
            <template v-if="mode === 'readOnly'">
                <div style="height: 32px"/>
            </template>
            <template v-else-if="mode === 'detail'">
                <b-button variant="outline-secondary"
                          class="mr-2"
                          @click="copyFromDetail"
                >
                    {{ $t('복사하여 추가') }}
                </b-button>
                <b-button key="btn-edit"
                          variant="outline-primary"
                          :disabled="!detailData.id"
                          @click="changeMode('edit')"
                >
                    {{ $t('수정') }}
                </b-button>
            </template>
            <template v-else-if="mode === 'add'">
                <b-button v-if="allowCopyImport"
                          variant="outline-secondary"
                          class="mr-2"
                          @click="$emit('request-copy-import')"
                >
                    {{ $t('다른 스키마에서 복사') }}
                </b-button>
                <b-button key="btn-save-add"
                          variant="outline-primary"
                          form="schema-input-form"
                          type="submit"
                >
                    {{ $t('저장') }}
                </b-button>
            </template>
            <template v-else-if="mode === 'edit'">
                <b-button variant="outline-danger"
                          :disabled="deleting"
                          @click="confirmDelete"
                >
                    <b-spinner v-if="deleting" small/>
                    {{ $t('삭제') }}
                </b-button>
                <b-button key="btn-save-edit"
                          variant="outline-primary"
                          form="schema-input-form"
                          type="submit"
                >
                    {{ $t('저장') }}
                </b-button>
                <b-button variant="outline-secondary"
                          @click="cancelEdit"
                >
                    {{ $t('취소') }}
                </b-button>
            </template>
        </div>
        <div class="tabs-container">
            <b-tabs v-model="tabIndex" class="default-tabs" :class="{'with-actions': mode !== 'add'}" content-class="mt-3">
                <b-tab  :title="$t('기본 정보')">
                    <template v-if="loading">
                        <b-skeleton-img no-aspect class="empty-content"></b-skeleton-img>
                    </template>
                    <!-- 상세·허용값을 못 받으면 빈 수정 폼 대신 오류를 보여 준다(빈 허용값으로 저장되는 사고 방지) -->
                    <PageEmptyComponent v-else-if="loadError && mode !== 'add'" compact :error="loadError" @retry="fetchDetailData"/>
                    <template v-else>
                        <schema-input-detail v-if="mode === 'detail' || mode === 'readOnly'" :detail-data="detailData"
                                             :projects="projects"
                                             :allowed-values="allowedValues"
                        />
                        <template v-else>
                            <b-alert v-if="isCopy && mode === 'add'" show variant="warning" class="mb-3 py-2">
                                <strong>{{ copySourceLabel }}</strong> 에서 복사됨 —
                                <template v-if="lockProjectToSelected">현재 프로젝트에 등록됩니다. 저장을 누르면 반영됩니다.</template>
                                <template v-else>연결 프로젝트를 선택하면 저장할 수 있습니다.</template>
                            </b-alert>
                            <schema-input-form ref="schemaInputForm"
                                               :key="`${mode}-${isCopy ? 'copy' : (detailId == null ? 'new' : detailId)}`"
                                               :mode="mode"
                                               :detail-data="isCopy ? copySeed : detailData"
                                               :projects="projects"
                                               :prop-allowed-values="isCopy ? copyAllowedValues : allowedValues"
                                               :propSelectedProjectCodeId="selectedProjectCodeId"
                                               :is-copy="isCopy"
                                               :lock-project-to-selected="lockProjectToSelected"
                                               @updateDetailData="updateDetailData"
                                               @refreshAllowedValues="refreshAllowedValuesHandler"
                                               @dirty="v => formDirty = v"
                            />
                        </template>
                    </template>
                </b-tab>
                <b-tab v-if="(mode === 'detail' || mode === 'readOnly') && !loadError" 
                       :title="$t('입력 값 검증')">
                    <template v-if="loading">
                        <b-skeleton-img no-aspect class="empty-content"></b-skeleton-img>
                    </template>
                    <template v-else>
                        <schema-input-validate  :detail-data="detailData"
                                               :projects="projects"
                                               :allowed-values="allowedValues"
                        />
                    </template>
                </b-tab>
            </b-tabs>
        </div>
    </b-modal>
</template>

<script>
import {deleteInputKey, getInputKey, listAllowedValues} from '@shared_utils/api/projectSchema';

import SchemaInputDetail
    from '@shared/components/admin/pages/Management/ProjectSchema/SchemaInput/SchemaInputDetail.vue';
import SchemaInputForm from '@shared/components/admin/pages/Management/ProjectSchema/SchemaInput/SchemaInputForm.vue';
import SchemaInputValidate
    from '@shared/components/admin/pages/Management/ProjectSchema/SchemaInput/SchemaInputValidate.vue';
import {getTargetProjectText} from '@shared/components/admin/pages/Management/common-data';
import {getErrorMessage} from '@core/utils/api/errorMessage';
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import unsavedChangesGuard from '@shared/mixin/unsaved-changes-guard';

const CLOSE_CONFIRM = {title: '저장하지 않은 입력', message: '입력한 내용이 저장되지 않았습니다. 닫을까요?'};
const EDIT_CANCEL_CONFIRM = {title: '저장하지 않은 입력', message: '수정한 내용이 저장되지 않았습니다. 수정을 취소할까요?'};

export default {
    name: 'SchemaInputModal',
    components: {
        PageEmptyComponent,
        SchemaInputValidate,
        SchemaInputForm,
        SchemaInputDetail,
    },
    mixins: [unsavedChangesGuard],
    props: {
        detailId: {
            type: [Number, String, null],
            default: null,
        },
        projects: {
            type: [Array, null],
            default: null,
        },
        selectedProjectCodeId: {
            type: [Number, String, null],
            default: null,
        },
        // add 모드에서 "다른 스키마에서 복사" 버튼 노출 여부. 배정 콘솔에서만 true.
        // 버튼 클릭 시 request-copy-import 이벤트만 emit 하고, 실제 목록/복사는
        // 부모가 picker 모달로 처리한 뒤 openAsCopy(id) 를 호출한다.
        allowCopyImport: {
            type: Boolean,
            default: false,
        },
        // 연결 프로젝트를 selectedProjectCodeId 로 고정+잠금(복사 포함). 콘솔에서만 true.
        lockProjectToSelected: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            mode: 'detail', // detail | add | edit | readOnly
            tabIndex: 1,
            detailData: {id: null},
            detailLoading: false,
            copyLoading: false,
            loadError: '',
            // 다른 질문으로 바꾸거나 새로 열면 진행 중이던 이전 응답을 버리기 위한 요청 순번
            detailSeq: 0,
            copySeq: 0,
            deleting: false,
            allowedValues: [],
            isCopy: false,
            copySeed: null,
            copyAllowedValues: [],
            copySourceLabel: null,
            formDirty: false,
            confirmingClose: false,
        };
    },
    computed: {
        loading() {
            return this.detailLoading || this.copyLoading;
        },
        // 등록·수정 폼에서 값이 바뀌었는지 (폼이 @dirty 로 알려 준다)
        hasUnsavedChanges() {
            return (this.mode === 'add' || this.mode === 'edit') && this.formDirty;
        },
        modalTitle() {
            if (this.mode === 'add') {
                return this.isCopy ? this.$t('스키마 입력 값 복사 등록') : this.$t('스키마 입력 값 등록');
            } else if (this.mode === 'edit') {
                return this.$t('스키마 입력 값 수정');
            } else {
                return this.$t('스키마 입력 값 상세');
            }
        },
        readOnly() {
            return this.mode === 'readOnly';
        }
    },
    watch: {
        detailId: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.fetchDetailData();
                } else {
                    this.detailSeq += 1;
                    this.detailLoading = false;
                    this.loadError = '';
                    this.detailData = {id: null};
                }
            }
        }
    },
    methods: {
        changeMode(mode) {
            this.cancelCopyLoad();
            this.mode = mode;
            this.tabIndex = 1;
        },
        // 진행 중인 복사 조회를 버린다(닫기·다른 대상 열기)
        cancelCopyLoad() {
            this.copySeq += 1;
            this.copyLoading = false;
        },
        buildCopySeedFrom(src, allowedValues) {
            this.copySeed = {
                key_name: src.key_name,
                value_type: src.value_type,
                label_key: src.label_key,
                label_texts: src.label_texts,
                is_required: src.is_required,
                description: src.description,
            };
            // 배너용 소스 라벨: 키 이름 + 프로젝트 정보 조합
            const projectText = getTargetProjectText(src.project_id, this.projects);
            this.copySourceLabel = projectText ? `${src.key_name} (${projectText})` : src.key_name;
            this.copyAllowedValues = (allowedValues || []).map((v, i) => ({
                __uid: `copy-${i}-${v.label_key || v.actual_value || 'av'}-${Date.now()}`,
                actual_value: v.actual_value,
                label_key: v.label_key,
                label_texts: v.label_texts || {},
            }));
        },
        // 상세 모드: 이미 로드된 detailData/allowedValues 재사용 (재조회 없음)
        copyFromDetail() {
            this.buildCopySeedFrom(this.detailData, this.allowedValues);
            this.isCopy = true;
            this.changeMode('add'); // 폼 fresh mount (key: add-copy)
        },
        // 목록 진입: 소스 id로 직접 조회 후 시드 (조회 완료 후 mode 전환 → fresh mount 보장)
        async openAsCopy(sourceId) {
            const seq = ++this.copySeq;
            try {
                this.copyLoading = true;
                this.tabIndex = 1;
                const res = await getInputKey(sourceId);
                const src = res.data;
                const av = src.value_type === 'list' ? await this.loadAllowedValues(sourceId) : [];
                if (seq !== this.copySeq) return;
                this.buildCopySeedFrom(src, av);
                this.isCopy = true;
                this.mode = 'add';
            } catch (e) {
                if (seq !== this.copySeq) return;
                console.error(e);
                if (e?.response?.status === 401) return;
                this.$EventBus.$emit('toast-failure', this.$t('스키마 입력 값 복사'), this.$t('복사할 스키마 입력 값을 불러오는 중 오류가 발생했습니다. 개발 팀에 문의해주세요.'));
                this.closeModal();
            } finally {
                if (seq === this.copySeq) this.copyLoading = false;
            }
        },
        // 배정 콘솔 '질문 만들기': 규칙이 쓰는 키 이름을 채운 등록 폼을 연다(저장은 사용자가 직접)
        openAsNew(seed) {
            this.cancelCopyLoad();
            this.detailSeq += 1;
            this.detailLoading = false;
            this.loadError = '';
            this.isCopy = false;
            this.copySeed = null;
            this.copyAllowedValues = [];
            this.copySourceLabel = null;
            this.detailData = {id: null, ...seed};
            this.changeMode('add');
        },
        async cancelEdit() {
            if (!(await this.confirmDiscardChanges(EDIT_CANCEL_CONFIRM))) return;
            this.changeMode('detail');
        },
        // X·ESC·바깥 클릭·hide() 모두 이리로 온다. 입력이 바뀌었으면 확인 후 닫는다(화면 정리는 hidden 에서)
        onHide(bvEvent) {
            if (!this.hasUnsavedChanges && !this.confirmingClose) return;
            bvEvent.preventDefault();
            if (this.confirmingClose) return;
            this.confirmingClose = true;
            this.confirmDiscardChanges(CLOSE_CONFIRM).then((ok) => {
                this.confirmingClose = false;
                if (ok) this.closeModal();
            });
        },
        onHidden() {
            this.cancelCopyLoad();
            this.mode = 'detail';
            this.isCopy = false;
            this.copySeed = null;
            this.copyAllowedValues = [];
            this.copySourceLabel = null;
            // 콘솔이 채워 둔 키 이름 등 신규용 값이 다음 '추가' 에 남지 않게 한다(상세 조회 결과는 유지)
            if (this.detailId == null) this.detailData = {id: null};
        },
        // 저장·삭제 뒤처럼 코드에서 닫을 때는 미저장 확인을 묻지 않는다
        closeModal() {
            this.formDirty = false;
            this.$bvModal.hide('schema-input-detail-modal');
        },
        async fetchDetailData() {
            const seq = ++this.detailSeq;
            try {
                this.detailLoading = true;
                this.loadError = '';
                this.detailData = {id: null};
                this.allowedValues = [];
                const res = await getInputKey(this.detailId);
                const detail = res.data;
                // 허용값까지 받은 뒤 함께 반영한다 — 허용값이 빈 채로 수정 폼이 열려 저장 때 지워지는 일이 없게
                const allowed = detail.value_type === 'list' ? await this.loadAllowedValues(detail.id) : [];
                if (seq !== this.detailSeq) return;
                this.detailData = detail;
                this.allowedValues = allowed;
            } catch (e) {
                if (seq !== this.detailSeq) return;
                console.error(e);
                if (e?.response?.status === 401) return;
                this.loadError = e?.response?.status === 404
                    ? '존재하지 않는 항목입니다.'
                    : getErrorMessage(e, '스키마 입력 값 정보를 불러오지 못했습니다.');
                this.$EventBus.$emit('toast-failure', this.$t('스키마 입력 값 상세'), this.loadError);
            } finally {
                if (seq === this.detailSeq) this.detailLoading = false;
            }
        },
        async loadAllowedValues(inputKeyId) {
            const res = await listAllowedValues({
                params: {
                    input_key_id: inputKeyId,
                    active_only: true
                }
            });
            const {allowed_values} = res.data || {};
            if (!Array.isArray(allowed_values)) return [];
            const mapped = allowed_values.map((v, i) => {
                const texts = v.label_texts || {};
                return {
                    __uid: `${v.label_key || v.actual_value || 'av'}-${i}-${Date.now()}`,
                    id: v.id,
                    actual_value: v.actual_value,
                    label_key: v.label_key,
                    label_texts: texts,
                    label_texts_ko: texts.ko ?? '',
                    label_texts_en: texts.en ?? '',
                    label_texts_ca: texts.ca ?? '',
                    sort_order: v.sort_order,
                };
            });
            mapped.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
            return mapped;
        },
        // 저장 뒤·저장 실패 뒤 다시 받는다(실패하면 빈 목록 — 예전 동작)
        async fetchAllowedValues() {
            try {
                this.allowedValues = await this.loadAllowedValues(this.detailData.id);
            } catch (e) {
                this.allowedValues = [];
            }
        },
        async refreshAllowedValuesHandler(done) {
            try {
                await this.fetchAllowedValues();
                if (this.$refs.schemaInputForm && typeof this.$refs.schemaInputForm.reloadFromProps === 'function') {
                    // 갱신된 allowedValues 를 명시적으로 전달.
                    // propAllowedValues 의 prop 반영은 다음 tick 에 일어나므로 stale 일 수 있다.
                    this.$refs.schemaInputForm.reloadFromProps(this.allowedValues);
                }
            } finally {
                if (typeof done === 'function') {
                    done();
                }
            }
        },
        async updateDetailData(detailData) {
            // 저장이 끝났으므로 이후 닫기에 확인을 묻지 않는다
            this.formDirty = false;
            if (this.mode === 'add') {
                this.$emit('addData', detailData);
                this.closeModal();
            } else if (this.mode === 'edit') {
                this.$emit('updateDetailData', detailData);
                this.detailData = detailData;
                await this.fetchAllowedValues();
                this.changeMode('detail');
            }
        },
        // 서버가 배정 규칙 참조로 거절하면(400 평문) 그 사유를, 아니면 기본 문구를 보여 준다.
        deleteErrorText(error, fallback) {
            const reason = getErrorMessage(error, '');
            return reason.includes('referenced by campaign rule sets')
                ? '배정 규칙에서 쓰는 질문이라 삭제할 수 없습니다. 규칙을 먼저 고쳐 주세요.' + ` (${reason})`
                : (reason || fallback);
        },
        confirmDelete() {
            if (this.deleting) return;
            const h = this.$createElement;
            const {key_name, label_key, project_id} = this.detailData;
            this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: this.$t('스키마 입력 값 삭제'),
                message: h('div', {style: {whiteSpace: 'pre-line'}},
                    `질문 키: ${key_name} / 라벨: ${label_key}
프로젝트: ${getTargetProjectText(project_id, this.projects)}
이 질문을 쓰는 배정 규칙이 있으면 삭제되지 않습니다. 삭제하면 되돌릴 수 없습니다.`)
            })
                .then((v) => {
                    if (v) {
                        this.deleteCondition();
                    }
                });
        },
        deleteCondition() {
            this.deleting = true;
            deleteInputKey(this.detailData.id)
                .then(() => {
                    this.$EventBus.$emit('toast-success', this.$t('스키마 입력 값 삭제'), this.$t('스키마 입력 값가 성공적으로 삭제되었습니다.'));
                    this.$emit('deleteData', this.detailData.id);
                    this.closeModal();
                })
                .catch((error) => {
                    console.error(error);
                    if (error?.response?.status === 401) return;
                    this.$EventBus.$emit('toast-failure', this.$t('스키마 입력 값 삭제'), this.deleteErrorText(error, this.$t('스키마 입력 값 삭제 중 오류가 발생했습니다. 개발 팀에 문의해주세요.')));
                })
                .finally(() => {
                    this.deleting = false;
                });
        }
    },
};
</script>

<style scoped lang="scss">

</style>
